import styled from 'styled-components';
import { useCallback } from 'react';
import { Executive } from 'Types/PersonTypes';
import {
  NavigateFunction, useLocation, useNavigate, useSearchParams,
} from 'react-router-dom';
import HeadingWithShowAll from 'Components/globalSearch/components/HeadingWithShowAll';
import SectionHeading from 'Components/globalSearch/components/SectionHeading';
import {
  selectSearchLoaded, selectPersonLoaded,
  selectIsPagination,
} from 'Selector/GlobalSearchResultsSelector';
import { useDispatch, useSelector } from 'react-redux';
import {
  minWidthApp,
  primaryColor, primaryHeadingColor, searchLoaderGrid, secondaryFont, wMedium,
} from 'Utils/utilsStyle';
import {
  SlicerResultPage, backgroundColorParsing, formatNumberWithCommas,
  isGuest, isSalesforce, valueToObject,
} from 'Utils/UtilityFunctions';
import { Col, Row } from 'antd';
import classNames from 'classnames';
import DragWrapper from 'Components/people/components/DragWrapper';
import {
  ONBOARDING, NODATAFOUND, SEARCH, FEED_GILROY_ROUTE,
  SEARCH_ROUTE,
} from 'Utils/Constants';
import { actions as certificationActions } from 'Slice/CertificationSlice';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { SearchResultsRouteState } from 'Pages/search/GlobalSearchResults';
import PeopleCardLarge from './PeopleCardLarge';
import SearchPersonSkeletonBlock from '../skeleton/SearchPersonSkeletonBlock';
import PersonSkeleton from '../skeleton/PersonSkeleton';

type Props = {
  heading?: string,
  hideDefaultBackground?: boolean,
  peopleCollection?: Executive[],
  loading?: boolean,
  query?: string,
  invertFollow?: boolean,
  showLoaderForLessResults?: boolean,
  numberOfLoaderForLessResults?: number,
  peopleCount?: Executive[],
  count?: number,
  bothSearchLoading?: boolean
  hideShowAll?: boolean
  isDrag?: (d: boolean) => void
  inGlobalSearch?: boolean,
  lazyMountCards?: boolean,
  primarySearchLoaded?: boolean,
  extendedSearchLoaded?: boolean,
  isPaginationOverride?: boolean,
} & typeof defaultProps;

const defaultProps = {
  hideDefaultBackground: false,
  loading: false,
  peopleCollection: [] as Executive[],
  heading: '',
  query: '',
  invertFollow: false,
  showLoaderForLessResults: false,
  numberOfLoaderForLessResults: 0,
  peopleCount: [] as Executive[],
  count: 0,
  bothSearchLoading: false,
  hideShowAll: false,
  isDrag: (d: boolean) => { console.log(d); },
  inGlobalSearch: false,
  lazyMountCards: true,
};

const StyleWrapper = styled.div`
  .fancy-box {
    border-radius: 22px;
    box-shadow: 0 0 60px rgba(0, 0, 0, 0.14);
    background: rgb(255, 255, 255);
    padding: 40px 50px 40px 50px;
    margin-top: 50px;
    h1 {
      font-family: "Roboto Slab";
      color: #424b50;
      font-size: 28px;
    }
    p {
      font-family: "Roboto";
      color: #5e7079;
    }
    input {
      font-size: 16px;
      padding: 7px 12px;
    }
    label {
      font-size: 12px;
    }
  }
  .search-page-people-skeleton {
    padding-top:20px;
    .person-skeleton-loader {
      @media all and (max-width: ${minWidthApp}) {
        .person-box {
          width: 100px;
          height: 100px;
          min-width: 100px;
        }
        .badge-box {
          width: 80px;
        }
        .title-box-short {
          width: 70px;
        }
        .btn {
          height: 20px;
        }
      }
    }
  }
`;

const LoaderWrapper = styled.div`
  .main {
    background:white;
    border: 1px solid ${primaryColor};
    padding: 40px 10px;
    text-align: center;
    border-radius: 8px;
    margin: 0px 5px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    .loader-style{
      height: 87px;
      width: 175px;
    }
    .text {
      font-size: 16px;
      color:${primaryHeadingColor};
      font-weight: ${wMedium};
      font-family: ${secondaryFont};
      letter-spacing: -0.45px;
      line-height: 20px;
      margin-top: 20px;
      margin-bottom: 5px;
      width:160px;
    }
  }
`;

const PeopleSection = function PeopleSection(props: Props) {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const extendedSections = ['person', 'company', 'article'];
  const extendedSection = (searchParams.get('extend') ?? '');
  const isDetailedSearch: boolean = extendedSections.includes(extendedSection);
  const navigate: NavigateFunction = useNavigate();
  const globalSearchLoaded = useSelector(selectSearchLoaded);
  const personsLoaded = useSelector(selectPersonLoaded);
  const isPagination: boolean = useSelector(selectIsPagination);
  const slicerNumber: number = SlicerResultPage();
  const { pathname, search } = useLocation();
  const host = window.location.href;
  const {
    heading, hideDefaultBackground, peopleCollection,
    loading, query, invertFollow,
    showLoaderForLessResults,
    numberOfLoaderForLessResults,
    peopleCount,
    count,
    bothSearchLoading,
    hideShowAll,
    isDrag,
    inGlobalSearch,
    lazyMountCards,
    primarySearchLoaded,
    extendedSearchLoaded,
    isPaginationOverride,
  } = props;

  const primaryLoaded = typeof primarySearchLoaded === 'boolean'
    ? primarySearchLoaded
    : globalSearchLoaded;
  const extendedLoaded = typeof extendedSearchLoaded === 'boolean'
    ? extendedSearchLoaded
    : personsLoaded;
  const paginationActive = typeof isPaginationOverride === 'boolean'
    ? isPaginationOverride
    : isPagination;
  const shouldShowPaginationSkeleton = paginationActive
    && (isDetailedSearch || typeof isPaginationOverride === 'boolean');

  const formattedCount = formatNumberWithCommas(count);
  const location = useLocation();
  const { state } = location as { state: SearchResultsRouteState };
  const previousPath = (state)?.previousPath ?? FEED_GILROY_ROUTE;

  const createPeopleRoute = () => {
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('searchPage_people_see_all_clicked', 0, {
        host,
      }),
    ));
    window.scrollTo(0, 0);
    let path = `${pathname}?query=${query}&extend=person`;
    const groupId = searchParams.get('groupId');
    if (search.includes('groupId') && groupId && parseInt(groupId, 10) > 0) {
      path = `${path}&groupId=${groupId}`;
    }
    return navigate(path, { state: { enableBackButton: true, previousPath } });
  };

  const navigateToSearchResult = () => {
    const path = `${SEARCH_ROUTE}${query}`;
    navigate(path, {
      state: isDetailedSearch ? { enableBackButton: true, previousPath } : { previousPath },
    });
  };

  const loadersArray = [];
  if (showLoaderForLessResults) {
    for (let i = 0; i < numberOfLoaderForLessResults; i += 1) {
      loadersArray.push(<PersonSkeleton />);
    }
  }
  const personCardPlaceholder = <PersonSkeleton diameterSm="120px" hideBtn />;

  const handleOnClick = useCallback(({ personId, personType, personName }: Executive) => {
    dispatch(certificationActions.setSearchPersonClicked(true));
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('searchPage_person_clicked', 0, {
        person_id: personId,
        person_name: personName,
        type: personType,
        host,
      }),
    ));
  }, [dispatch, host]);
  return (
    (
      peopleCollection.length > 0 || loading || (primaryLoaded && !extendedLoaded) ? (
        <StyleWrapper>
          {
            loading
              ? (
                <SearchPersonSkeletonBlock
                  sliceFlag={!isDetailedSearch}
                  inGlobalSearch={inGlobalSearch}
                />
              )
              : (
                <>
                  <div className={` ${hideDefaultBackground ? '' : 'fancy-box'}`}>
                    <HeadingWithShowAll
                      inGlobalSearch={inGlobalSearch}
                      isShowAllHidden={hideShowAll || (isDetailedSearch ? true
                        : peopleCount && peopleCount.length <= slicerNumber)}
                      leftHeading={(
                        <SectionHeading customClassName="global-search-heading">{`${heading} (${formattedCount})`}</SectionHeading>)}
                      onClick={createPeopleRoute}
                      onSeelessClick={navigateToSearchResult}
                      isDetailedSearch={!hideShowAll && isDetailedSearch}
                    />

                    <Row
                      className={classNames('Grid-width-row', { gridUpdated: inGlobalSearch })}
                      gutter={[{
                        xs: 16, lg: 40, md: 32, sm: 32,
                      }, 8]}
                    >
                      {window.location.pathname?.includes(`/${ONBOARDING}`) || isSalesforce()
                        ? peopleCollection?.map((person: Executive) => (
                          <Col key={person?.personId} span={4} xs={6} sm={6} md={4} className="gutter-row">
                            <PeopleCardLarge
                              noFollow={isGuest()}
                              personName={person.personName}
                              logo={person.personImage}
                              persona={person.archeType.color}
                              tag={
                                person.personDesignation
                                  ? person.personDesignation : person.headline
                              }
                              follow={invertFollow ? !person.personAdded : person.personAdded}
                              personaType={person.archeType.name}
                              hidePersonality={false}
                              personAddedFlag={person.personAdded}
                              personId={person.personId}
                              personality={person.personType}
                              wrapperColor={backgroundColorParsing(person.personalityTypes)}
                              personalityTypesColors={person.personalityTypes}
                              isBtnAdd
                              company={person.companyName}
                              disableNavigation={pathname.includes(ONBOARDING)}
                              isSearchPage={pathname.includes(SEARCH)}
                              isLivePerson={person.personType === 'LIVESEARCH'}
                              inGlobalSearch={inGlobalSearch}
                              companyId={person?.companyId}
                              location={person?.location}
                            />
                          </Col>
                        ))
                        : peopleCollection?.map((person: Executive) => {
                          const personCard = (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={() => handleOnClick(person)}
                              onKeyPress={() => handleOnClick(person)}
                              aria-label="button"
                            >
                              <DragWrapper key={person?.personId} isDrag={isDrag}>
                                <PeopleCardLarge
                                  showDrawerIcon={!!person?.personNotes}
                                  noFollow={isGuest()}
                                  personName={person.personName}
                                  logo={person.personImage}
                                  persona={person.archeType.color}
                                  tag={person.personDesignation ? person.personDesignation
                                    : person.headline}
                                  follow={invertFollow ? !person.personAdded : person.personAdded}
                                  personaType={person.archeType.name}
                                  hidePersonality={false}
                                  personAddedFlag={person.personAdded}
                                  personId={person.personId}
                                  personality={person.personType}
                                  wrapperColor={backgroundColorParsing(person.personalityTypes)}
                                  personalityTypesColors={person.personalityTypes}
                                  isBtnAdd
                                  company={person.companyName}
                                  disableNavigation={pathname.includes(ONBOARDING)}
                                  executiveType={person.executiveType}
                                  isSearchPage={pathname.includes(SEARCH)}
                                  isLivePerson={person.personType === 'LIVESEARCH'}
                                  inGlobalSearch={inGlobalSearch}
                                  companyId={person?.companyId}
                                  location={person?.location}
                                />
                              </DragWrapper>
                            </span>
                          );

                          return (
                            <Col span={4} xs={6} sm={6} md={4} className="gutter-row list-perf-item" key={person?.personId}>
                              {personCard}
                            </Col>
                          );
                        })}
                      <Col span={4} xs={6} sm={6} md={4}>
                        {(primaryLoaded && !extendedLoaded && peopleCollection.length < 6)
                          && (
                            <LoaderWrapper className="loader-wrapper">
                              <div className="main">
                                <img className="loader-style" src={searchLoaderGrid} alt="extended-loader" />
                              </div>
                            </LoaderWrapper>
                          )}
                      </Col>

                      {primaryLoaded && extendedLoaded && !peopleCollection.length
                        && <h6>{NODATAFOUND}</h6>}
                    </Row>

                  </div>
                  {shouldShowPaginationSkeleton && <SearchPersonSkeletonBlock />}

                </>
              )
          }
        </StyleWrapper>
      ) : (
        <>
          {' '}
          {isDetailedSearch
            && !peopleCollection.length
            && bothSearchLoading
            && <SearchPersonSkeletonBlock />}
          {' '}
        </>
      )
    )

  );
};

PeopleSection.defaultProps = defaultProps;
export default PeopleSection;
